import { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

const GET_TEMPLATES = gql`
  query MissionAdminMissionStepTemplates(
    $channel: Channel
    $missionType: MissionType
  ) {
    missionAdminMissionStepTemplates(
      channel: $channel
      missionType: $missionType
    ) {
      id
      missionType
      channel
      title
      guide
      requirements
      notices
      proofType
      createdAt
      updatedAt
    }
  }
`;

const UPSERT_TEMPLATE = gql`
  mutation MissionAdminUpsertMissionStepTemplate(
    $input: MissionAdminUpsertMissionStepTemplateInput!
  ) {
    missionAdminUpsertMissionStepTemplate(input: $input) {
      id
      missionType
      channel
      title
      guide
      requirements
      notices
      proofType
    }
  }
`;

const MissionAdminTemplates = () => {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedMissionType, setSelectedMissionType] = useState<string | null>(
    null
  );
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);

  const { data, loading, error, refetch } = useQuery(GET_TEMPLATES, {
    variables: {
      channel: selectedChannel || undefined,
      missionType: selectedMissionType || undefined,
    },
  });

  const [upsertTemplate, { loading: saving }] = useMutation(UPSERT_TEMPLATE, {
    onCompleted: () => {
      setEditingTemplate(null);
      refetch();
    },
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // 빈 문자열 필터링
      const guide = (editingTemplate.guide || []).filter(
        (item: string) => item.trim() !== ""
      );
      const requirements = (editingTemplate.requirements || []).filter(
        (item: string) => item.trim() !== ""
      );
      const notices = (editingTemplate.notices || []).filter(
        (item: string) => item.trim() !== ""
      );

      await upsertTemplate({
        variables: {
          input: {
            missionType: editingTemplate.missionType,
            channel: editingTemplate.channel || null,
            title: editingTemplate.title || null,
            guide: guide,
            requirements: requirements,
            notices: notices,
            proofType: editingTemplate.proofType,
          },
        },
      });
      alert("템플릿이 저장되었습니다.");
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const getChannelLabel = (channel: string | null) => {
    if (!channel) return "기본";
    const labels: Record<string, string> = {
      NAVER: "네이버",
      COUPANG: "쿠팡",
      CARROT: "당근",
    };
    return labels[channel] || channel;
  };

  const getMissionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      SEARCH: "검색",
      LIKE: "찜",
      ADD_TO_CART: "장바구니",
      PURCHASE: "구매",
      REVIEW: "리뷰",
      PHOTO_REVIEW: "포토리뷰",
      USAGE_SHOT: "사용샷 인증",
      ETC: "기타",
    };
    return labels[type] || type;
  };

  const getProofTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      NONE: "없음",
      SCREENSHOT: "스크린샷",
      PHOTO: "사진",
      TEXT: "텍스트",
      URL: "URL",
    };
    return labels[type] || type;
  };

  if (loading) return <div>로딩 중...</div>;
  if (error) return <div>에러: {error.message}</div>;

  const templates = data?.missionAdminMissionStepTemplates || [];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">미션 템플릿 관리</h1>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              채널
            </label>
            <select
              value={selectedChannel || ""}
              onChange={(e) => setSelectedChannel(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">전체</option>
              <option value="NAVER">네이버</option>
              <option value="COUPANG">쿠팡</option>
              <option value="CARROT">당근</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              미션 타입
            </label>
            <select
              value={selectedMissionType || ""}
              onChange={(e) => setSelectedMissionType(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">전체</option>
              <option value="SEARCH">검색</option>
              <option value="LIKE">찜</option>
              <option value="ADD_TO_CART">장바구니</option>
              <option value="PURCHASE">구매</option>
                  <option value="REVIEW">리뷰</option>
                  <option value="PHOTO_REVIEW">포토리뷰</option>
                  <option value="USAGE_SHOT">사용샷 인증</option>
                  <option value="ETC">기타</option>
            </select>
          </div>
        </div>
      </div>

      {/* 템플릿 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                채널
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                미션 타입
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                제목
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                인증 방식
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map((template: any) => (
              <tr key={template.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {getChannelLabel(template.channel)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {getMissionTypeLabel(template.missionType)}
                </td>
                <td className="px-6 py-4 text-sm">{template.title || "-"}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {getProofTypeLabel(template.proofType)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => setEditingTemplate(template)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    편집
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {templates.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            등록된 템플릿이 없습니다.
          </div>
        )}
      </div>

      {/* 새 템플릿 추가 버튼 */}
      <div className="mt-6">
        <button
          onClick={() =>
            setEditingTemplate({
              missionType: "",
              channel: null,
              title: "",
              guide: [],
              requirements: [],
              notices: [],
              proofType: "NONE",
            })
          }
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          새 템플릿 추가
        </button>
      </div>

      {/* 편집 모달 */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">템플릿 편집</h2>
            <form onSubmit={handleSave}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    미션 타입 *
                  </label>
                  <select
                    required
                    value={editingTemplate.missionType}
                    onChange={(e) =>
                      setEditingTemplate({
                        ...editingTemplate,
                        missionType: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">선택하세요</option>
                    <option value="SEARCH">검색</option>
                    <option value="LIKE">찜</option>
                    <option value="ADD_TO_CART">장바구니</option>
                    <option value="PURCHASE">구매</option>
                  <option value="REVIEW">리뷰</option>
                  <option value="PHOTO_REVIEW">포토리뷰</option>
                  <option value="USAGE_SHOT">사용샷 인증</option>
                  <option value="ETC">기타</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    채널
                  </label>
                  <select
                    value={editingTemplate.channel || ""}
                    onChange={(e) =>
                      setEditingTemplate({
                        ...editingTemplate,
                        channel: e.target.value || null,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">기본 (전체)</option>
                    <option value="NAVER">네이버</option>
                    <option value="COUPANG">쿠팡</option>
                    <option value="CARROT">당근</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    제목
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.title || ""}
                    onChange={(e) =>
                      setEditingTemplate({
                        ...editingTemplate,
                        title: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    인증 방식 *
                  </label>
                  <select
                    required
                    value={editingTemplate.proofType}
                    onChange={(e) =>
                      setEditingTemplate({
                        ...editingTemplate,
                        proofType: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="NONE">없음</option>
                    <option value="SCREENSHOT">스크린샷</option>
                    <option value="PHOTO">사진</option>
                    <option value="TEXT">텍스트</option>
                    <option value="URL">URL</option>
                  </select>
                </div>

                {/* Guide 입력 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    가이드 (단계별 안내)
                  </label>
                  <div className="space-y-2">
                    {(editingTemplate.guide || []).map(
                      (item: string, idx: number) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newGuide = [
                                ...(editingTemplate.guide || []),
                              ];
                              newGuide[idx] = e.target.value;
                              setEditingTemplate({
                                ...editingTemplate,
                                guide: newGuide,
                              });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="가이드 항목 입력"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newGuide = [
                                ...(editingTemplate.guide || []),
                              ];
                              newGuide.splice(idx, 1);
                              setEditingTemplate({
                                ...editingTemplate,
                                guide: newGuide,
                              });
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            삭제
                          </button>
                        </div>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTemplate({
                          ...editingTemplate,
                          guide: [...(editingTemplate.guide || []), ""],
                        });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + 가이드 항목 추가
                    </button>
                  </div>
                </div>

                {/* Requirements 입력 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    필수 요구사항
                  </label>
                  <div className="space-y-2">
                    {(editingTemplate.requirements || []).map(
                      (item: string, idx: number) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newRequirements = [
                                ...(editingTemplate.requirements || []),
                              ];
                              newRequirements[idx] = e.target.value;
                              setEditingTemplate({
                                ...editingTemplate,
                                requirements: newRequirements,
                              });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="필수 요구사항 입력"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newRequirements = [
                                ...(editingTemplate.requirements || []),
                              ];
                              newRequirements.splice(idx, 1);
                              setEditingTemplate({
                                ...editingTemplate,
                                requirements: newRequirements,
                              });
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            삭제
                          </button>
                        </div>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTemplate({
                          ...editingTemplate,
                          requirements: [
                            ...(editingTemplate.requirements || []),
                            "",
                          ],
                        });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + 요구사항 추가
                    </button>
                  </div>
                </div>

                {/* Notices 입력 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    주의사항 (꼭 알아야 할 점)
                  </label>
                  <div className="space-y-2">
                    {(editingTemplate.notices || []).map(
                      (item: string, idx: number) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newNotices = [
                                ...(editingTemplate.notices || []),
                              ];
                              newNotices[idx] = e.target.value;
                              setEditingTemplate({
                                ...editingTemplate,
                                notices: newNotices,
                              });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="주의사항 입력"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newNotices = [
                                ...(editingTemplate.notices || []),
                              ];
                              newNotices.splice(idx, 1);
                              setEditingTemplate({
                                ...editingTemplate,
                                notices: newNotices,
                              });
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            삭제
                          </button>
                        </div>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTemplate({
                          ...editingTemplate,
                          notices: [...(editingTemplate.notices || []), ""],
                        });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + 주의사항 추가
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? "저장 중..." : "저장"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionAdminTemplates;
